/**
 * Archive-or-delete dialog for one conversation. Archive keeps it on disk and
 * searchable, out of the dashboard; Delete moves it to the OneStone trash
 * (restorable from Settings → Trash until the retention purge). When the
 * conversation still has messages that haven't been summarized, the dialog
 * offers "Summarize first" so knowledge isn't lost.
 */
import { Button } from '@onestone/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@onestone/ui/dialog';
import { chatStore, type ConversationSummary } from '@/lib/chatStore';

/** Summarize needs at least 2 live messages — mirrors the API guard. */
const MIN_SUMMARIZE_MESSAGES = 2;

interface ArchiveConversationDialogProps {
  conversation: ConversationSummary | null;
  onClose: () => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
}

export function ArchiveConversationDialog({
  conversation,
  onClose,
  onArchive,
  onDelete,
}: ArchiveConversationDialogProps) {
  if (!conversation) return null;
  const { id, title, messageCount } = conversation;
  const canSummarize = messageCount >= MIN_SUMMARIZE_MESSAGES;

  const handleSummarizeFirst = async (): Promise<void> => {
    onClose();
    await chatStore.switchConversation(id);
    void chatStore.startSummarize();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Archive or delete this conversation?</DialogTitle>
          <DialogDescription>
            Archive removes "{title}" from the dashboard and keeps its full history on disk, still searchable. Delete
            moves it to the trash: gone from lists and search, restorable from Settings → Trash until the retention
            period ends.
          </DialogDescription>
        </DialogHeader>

        {messageCount > 0 && (
          <div className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/20 px-3 py-2">
            <p className="text-sm text-foreground/90">
              {messageCount} {messageCount === 1 ? "message hasn't" : "messages haven't"} been summarized yet.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {canSummarize && (
            <Button variant="outline" onClick={() => void handleSummarizeFirst()}>
              Summarize first
            </Button>
          )}
          <Button
            variant="outline"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              onDelete(id);
              onClose();
            }}
          >
            Delete
          </Button>
          <Button
            onClick={() => {
              onArchive(id);
              onClose();
            }}
          >
            Archive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
